import {
  BorrowingDetail,
  BorrowingDetailActions,
  BorrowingDetailMaterials,
} from "@/components/borrowing/detail/BorrowingDetail";
import { Button } from "@/components/ui/button/Button";
import IBorrowingData from "@/models/borrowing/BorrowingData";
import axios, { isAxiosError } from "axios";
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { AcceptDialog } from "../components/dialog/AcceptDialog";
import { RejectDialog } from "../components/dialog/RejectDialog";
import { useUserStore } from "@/store/user/userStore";
import useMessierId from "@/lib/hooks/useMessierId";
import { FinalizeDialog } from "../components/dialog/FinalizeDialog";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import UserService from "@/lib/services/user/UserService";
import { IDetailBorrowing } from "@/models/borrowing/DetailBorrowing";
import ShiftService from "@/lib/services/shift/ShiftService";
import { AvailableShiftsResponse } from "@/models/shift/AvailableShiftsResponse";
import { useToast } from "@/lib/hooks/useToast";
import { UserFacingError } from "@/models/helper/UserFacingError";
import { StaffBorrowingDetailMaterials, StaffBorrowingDetailMaterialsRef } from "../components/material/StaffBorrowingDetailMaterials";
import { RUANG_LAB_FOODTECH_ALSUT } from "@/lib/constants/constants";
import { AllocatedMaterials } from "../../borrowing/components/material/AllocatedMaterials";
import StockOpnameService from "@/lib/services/borrowing/StockOpnameService";

export interface BatchData {
  id: number;
  brand: string;
  code: string;
}

export interface AllocationData {
  allocatedAmount: number;
  batch: BatchData;
}

export interface ItemData {
  id: number;
  name: string;
  sizeUnit: string;
}

export interface StaffMaterial {
  id: number;
  requestAmount: number;
  item: ItemData;
  allocations: AllocationData[];
}

export interface FetchedAllocation {
  batchId: number;
  brand: string;
  code: string;
  allocatedAmount: number;
}

export type FetchedMaterial = {
  id: number;
  name: string;
  requestAmount: number;
  unit: string;
  allocations: FetchedAllocation[];
};

interface RawAllocation {
  batchId: number;
  allocatedAmount: number;
  batch: {
    brand: string;
    code: string;
  };
}

interface RawDetail {
  id: number;
  requestAmount: number;
  item: {
    name: string;
    sizeUnit: string;
  };
  allocations?: RawAllocation[];
}

const StaffHistoryDetail = () => {
  const params = useParams();
  const { toast } = useToast();
  const [data, setData] = useState<IBorrowingData>();
  const [roomTransactions, setRoomTransactions] =
    useState<AvailableShiftsResponse>();
  const userStore = useUserStore();
  const { messierId } = useMessierId();
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState<StaffMaterial[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [studentMaterials, setStudentMaterials] = useState<FetchedMaterial[]>([]);
  const [draftNoUsageDetailIds, setDraftNoUsageDetailIds] = useState<number[]>([]);
  const [savedNoUsageDetailIds, setSavedNoUsageDetailIds] = useState<number[]>([]);
  const materialsRef = useRef<StaffBorrowingDetailMaterialsRef>(null);

  const fetchMaterials = async (borrowingId: string) => {
    setMaterialsLoading(true);
    try {
      const reqData = await StockOpnameService.getStaffRequestByBorrowing(borrowingId);
      if (reqData) {
        
        const requestObj = Array.isArray(reqData) ? reqData[0] : reqData;
        
        if (requestObj) {

          const details = requestObj.details || [];
          setMaterials(details);

          const extractedMaterials: FetchedMaterial[] = details.map((d: RawDetail) => ({
            id: d.id,
            name: d.item.name,
            requestAmount: d.requestAmount,
            unit: d.item.sizeUnit,
            allocations: d.allocations?.map((a: RawAllocation) => ({
              batchId: a.batchId,
              brand: a.batch.brand,
              code: a.batch.code,
              allocatedAmount: a.allocatedAmount
            })) || []
          }));
          setStudentMaterials(extractedMaterials);
        }
      }
    } catch (err) {
      console.error("Failed to fetch material requests.");
    } finally {
      setMaterialsLoading(false);
    }
  };

  const hasCompleteUsageSubmission = studentMaterials.length > 0 && studentMaterials.every(
    mat => {
      const hasPositiveAllocations = mat.allocations?.some(
        allocation => Number(allocation.allocatedAmount) > 0,
      ) ?? false;
      const isMarkedUnused = draftNoUsageDetailIds.includes(mat.id);
      const wasSavedAsUnused = savedNoUsageDetailIds.includes(mat.id);
      return isMarkedUnused
        ? wasSavedAsUnused && !hasPositiveAllocations
        : !wasSavedAsUnused && hasPositiveAllocations;
    },
  );

  const fetchData = async (id: string | undefined) => {
    const res = await axios.get(
      `${import.meta.env.VITE_API_URL}borrowing/data`,
      {
        params: { id: id },
      },
    );

    res.data.data.detail_borrowings = await Promise.all(
      res.data.data.detail_borrowings.map(async (d: IDetailBorrowing) => ({
        id: d.id,
        borrowing_id: d.borrowing_id,
        student_nim: d.student_nim,
        student_name: await UserService.getName(d.student_nim),
      })),
    );
    res.data.data.title = "Borrowing Detail";
    setData(res.data.data);

    if (res.data.data.room?.type?.id == RUANG_LAB_FOODTECH_ALSUT && id) {
      fetchMaterials(id);
    }
  };

  useEffect(() => {
    const id = params.id;
    if (id) {
      setDraftNoUsageDetailIds([]);
      setSavedNoUsageDetailIds([]);
      fetchData(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    let ignore = false;
    const fetchRoomTransactions = async () => {
      if (!data) return;

      try {
        const transactions = await ShiftService.getRoomTransactions(
          new Date(data.date),
          data.room.id.toString(),
          data.room.room_code,
          data.room.type?.id.toString(),
        );

        if (!ignore) setRoomTransactions(transactions);
      } catch (error) {
        if (error instanceof UserFacingError) {
          toast({ title: error.title, description: error.description });
        }
      }
    };

    fetchRoomTransactions();

    return () => {
      ignore = true;
    };
  }, [data, toast]);

  const handleSaveAllocations = async () => {
    setLoading(true);
    try {
      if (materialsRef.current && materials.length > 0) {
        const submission = materialsRef.current.getAllocationSubmission();
        const noUsageIds = new Set(submission.noUsageDetailIds);

        if (submission.allocations.some(payload => payload.allocations.length === 0 && !noUsageIds.has(payload.detailId))) {
          toast({
            title: "Error",
            description: "Assign a batch for each material, or select 'No material was used' for materials that were not used.",
          });
          setLoading(false);
          return;
        }
        
        await StockOpnameService.saveAllocations(submission.allocations);
        setDraftNoUsageDetailIds(submission.noUsageDetailIds);
        setSavedNoUsageDetailIds(submission.noUsageDetailIds);
        toast({ title: "Success", description: "Material allocations saved." });
        if (params?.id) await fetchMaterials(params.id);
      }
    } catch (error: unknown) {
      console.error(error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Unknown error occured.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (reason: string) => {
    setLoading(true);
    try {
      await axios.patch(
        import.meta.env.VITE_API_URL + "borrowing/staff/accept",
        null,
        {
          params: {
            id: params?.id,
            cid: messierId,
            username: userStore.data?.UserName,
            reason: reason,
          },
        },
      );
      
      toast({ title: "Success", description: "Borrowing accepted." });
      fetchData(params?.id);
    } catch (error: unknown) {
      console.error(error);
      toast({
        title: "Accept Borrowing Error",
        description: isAxiosError(error)
          ? error.response?.data.errors
          : error instanceof Error ? error.message : "Unknown error occured.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (reason: string, reason_type: string) => {
    setLoading(true);
    try {
      await axios.patch(
        import.meta.env.VITE_API_URL + "borrowing/staff/reject",
        null,
        {
          params: {
            id: params?.id,
            username: userStore.data?.UserName,
            reason: reason,
            reason_type: reason_type,
          },
        },
      );
      setLoading(false);
      fetchData(params?.id);
    } catch (error: unknown) {
      console.error("Unknown error:", error);
    }
  };

  const handleFinalize = async (status: string) => {
    setLoading(true);
    try {
      await axios.patch(
        import.meta.env.VITE_API_URL + "borrowing/staff/finalize",
        null,
        {
          params: {
            id: params?.id,
            status: status,
            username: userStore.data?.UserName,
            email: data?.userData?.email,
            description: status,
            student_nim_leader: data?.student_nim_leader,
            no_usage_detail_ids: status === "Present" ? savedNoUsageDetailIds.join(",") : undefined,
          },
        },
      );
      const hasPositiveMaterialUsage = studentMaterials.some(material =>
        material.allocations.some(allocation => Number(allocation.allocatedAmount) > 0),
      );
      toast({ title: "Success", description: status === "Present"
        ? hasPositiveMaterialUsage
          ? "Borrowing finalized and allocated stock deducted."
          : "Borrowing finalized. No stock was deducted because no material was used."
        : "Borrowing finalized." });
      if (params?.id) await fetchData(params.id);
    } catch (error) {
      console.error("Unknown error:", error);
      const responseError = isAxiosError(error) ? error.response?.data?.errors : null;
      toast({
        title: "Finalize Error",
        description: typeof responseError === "string"
          ? responseError
          : responseError?.message || (error instanceof Error ? error.message : "Could not finalize borrowing."),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingIndicator loading={loading} />
      <BorrowingDetail data={data}>
        {data?.borrowing_status === "Pending" && (
          <>
            {data?.room?.type?.id == RUANG_LAB_FOODTECH_ALSUT && (
              <BorrowingDetailMaterials 
                materials={studentMaterials} 
                isLoading={materialsLoading} 
              />
            )}
            
            <BorrowingDetailActions>
              <AcceptDialog
                title="Room"
                borrowingProps={{
                  transactions: roomTransactions,
                  currentBorrowerCount: data.userData.borrower_count,
                  shifts: data.detail_shifts,
                }}
                onAccept={handleAccept}
              />
              <RejectDialog
                title="Room"
                transactions={roomTransactions}
                onReject={handleReject}
              />
            </BorrowingDetailActions>
          </>
        )}
        {(data?.borrowing_status === "Accepted" || data?.borrowing_status === "Finalized") && (
          <>
            {data?.room?.type?.id == RUANG_LAB_FOODTECH_ALSUT && (
              <>
                {data.borrowing_status === "Accepted" && materials.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    <StaffBorrowingDetailMaterials 
                      ref={materialsRef}
                      materials={materials} 
                      isLoading={materialsLoading} 
                      onNoUsageSelectionChange={setDraftNoUsageDetailIds}
                    />
                    <div className="flex justify-end">
                      <Button variant="blue" onClick={handleSaveAllocations}>Save Allocations</Button>
                    </div>
                  </div>
                ) : (
                  <AllocatedMaterials 
                    materials={studentMaterials} 
                    isLoading={materialsLoading}
                    isFinalizedPresent={data.borrowing_status === "Finalized" && data.attendance_status === "Present"}
                  />
                )}
              </>
            )}
            
            {data?.borrowing_status === "Accepted" && (
              <BorrowingDetailActions>
                <FinalizeDialog
                  onFinalize={handleFinalize}
                  dialogTitle="Finalize"
                  missingUsageSubmission={studentMaterials.length > 0 && !hasCompleteUsageSubmission}
                  missingUsageSubmissionMessage="Save batch allocations, or explicitly mark each unused material as 'No material was used', before finalizing as Present."
                />
              </BorrowingDetailActions>
            )}
          </>
        )}
      </BorrowingDetail>
    </>
  );
};

export default StaffHistoryDetail;
